import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Image
} from "@react-pdf/renderer";
import Logo from "../../../assets/images/logo.png";
import formatNumber from "../../../helpers/NumberFormat";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Helvetica",
    lineHeight: 1.5
  },
  section: {
    marginBottom: 8
  },
  bold: {
    fontFamily: "Helvetica-Bold"
  },
  row: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    marginBottom: 5
  },
  col: {
    flex: 1,
    paddingRight: 4
  },
  borderBox: {
    border: "1px solid #aaa",
    borderRadius: 4,
    padding: 8,
    marginBottom: 4
  },
  centerText: {
    textAlign: "center"
  },
  logo: {
    width: "60%"
  },
  pageFooter: {
    marginTop: 50,
    textAlign: "center"
  },
  justifyText: {
    textAlign: "justify"
  }
});

const formatDate = (isoDate, type = "ddmmyyyy") => {
  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  switch (type) {
    case "ddmmyyyy":
      return `${day}.${month}.${year}`;
    case "yyyymmdd":
      return `${year}${month}${day}`;
    default:
      return isoDate;
  }
};

const getAccreditedDate = (selectedDateStr) => {
  const selectedDate = new Date(selectedDateStr);
  const selectedHour = selectedDate.getHours();

  let workingDaysToAdd = 2;

  if (selectedHour >= 12) {
    workingDaysToAdd = 3;
  }

  let resultDate = new Date(selectedDate);

  while (workingDaysToAdd > 0) {
    resultDate.setDate(resultDate.getDate() + 1);
    const day = resultDate.getDay();

    if (day !== 0 && day !== 6) {
      workingDaysToAdd--;
    }
  }

  return formatDate(resultDate, "ddmmyyyy");
};

const getAccountingDate = (crDT, exDT) => {
  const createdAt = new Date(crDT);
  const executedAt = new Date(exDT);
  const isSameDay = createdAt.toDateString() === executedAt.toDateString();
  const isAfterNoon = createdAt.getHours() >= 12;

  let finalDate = executedAt;

  if (isSameDay && isAfterNoon) {
    finalDate = new Date(createdAt);
    finalDate.setDate(finalDate.getDate() + 1);

    if (finalDate.getDay() === 6) {
      finalDate.setDate(finalDate.getDate() + 2);
    }

    if (finalDate.getDay() === 0) {
      finalDate.setDate(finalDate.getDate() + 1);
    }
  }

  return formatDate(finalDate, "ddmmyyyy");
}

const ReceiptPdf = ({ transaction, user }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.section}>
        <View style={styles.row}>
          <View style={[styles.col]}>
            <Image src={Logo} style={styles.logo} />
          </View>
          <View style={[styles.col, { fontSize: 6 }]}>
            <Text style={styles.justifyText}>Banque Artesia Nederland N.V. - Joint-stock cooperative company Website: https://artesiab.com/</Text>
            <Text style={styles.justifyText}>E-mail: administration@Banque Artesia Nederland N.V.-direct.com - Tax Code 09897919.</Text>
            <Text style={styles.justifyText}>Registration in the Company Register no. 00179660501.</Text>
            <Text style={styles.justifyText}>Member of the Group Modulr Finance B.V. registered in the Register of Banking Groups with Modulr Finance B.V. as parent company, which exercises its management and coordination.</Text>
            <Text style={styles.justifyText}>Company participating in the VAT Group Modulr Finance B.V. - VAT number 15240941007, SDI code. 9GHPWLV.</Text>
            <Text style={styles.justifyText}>Registered in the Register of cooperative societies no. R182870 - Member of the Cooperative Credit Depositors Guarantee Fund and the Fund National Guarantee.</Text>
          </View>
        </View>
      </View>

      {/* Order */}
      <View style={[styles.section, styles.borderBox]}>
        <Text style={[styles.centerText, { fontSize: 9, padding: 10 }]}>
          Order number INTER{formatDate(transaction.created_at, "yyyymmdd")}BOSBE{transaction.reference_code} - Order date {formatDate(transaction.created_at, "ddmmyyyy")}
        </Text>
      </View>

      {/* Current Account & Account Holder */}
      <View style={[styles.section, styles.borderBox]}>
        <View style={styles.row}>
          <View style={styles.col}><Text>Current Account</Text></View>
          <View style={styles.col}></View>
          <View style={styles.col}><Text>Account Holder</Text></View>
        </View>
        <View style={styles.row}>
          <View style={styles.col}><Text style={styles.bold}>{user.detail.account_number?.replace(/^.*?-/, '')}</Text></View>
          <View style={styles.col}></View>
          <View style={styles.col}><Text style={styles.bold}>{user.first_name?.toUpperCase() + ' ' + user.last_name?.toUpperCase()}</Text></View>
        </View>
      </View>

      {/* Beneficiary */}
      <View style={[styles.section, styles.borderBox]}>
        <Text>Beneficiary</Text>
        <Text style={styles.bold}>{transaction.beneficary_name?.toUpperCase()}</Text>

        <View style={[styles.row, { marginTop: 8 }]}>
          <View style={styles.col}><Text>IBAN</Text></View>
          <View style={styles.col}><Text>BIC/SWIFT</Text></View>
          <View style={styles.col}><Text>BANK</Text></View>
        </View>
        <View style={styles.row}>
          <View style={styles.col}><Text style={styles.bold}>{transaction.iban?.toUpperCase()}</Text></View>
          <View style={styles.col}><Text style={styles.bold}>{transaction.swift_code?.toUpperCase()}</Text></View>
          <View style={styles.col}><Text style={styles.bold}>{transaction.bank_name?.toUpperCase()}</Text></View>
        </View>
      </View>

      {/* TRN */}
      <View style={[styles.section, styles.borderBox]}>
        <Text>TRN</Text>
        <Text style={styles.bold}>{transaction.trn_nr}</Text>
      </View>

      {/* Dates */}
      <View style={[styles.section, styles.borderBox]}>
        <View style={styles.row}>
          <View style={styles.col}><Text>Settlement Date</Text></View>
          <View style={styles.col}><Text>Ordering accounting date</Text></View>
          <View style={styles.col}><Text>Ordering value date</Text></View>
        </View>
        <View style={styles.row}>
          <View style={styles.col}><Text style={styles.bold}>{formatDate(transaction.created_at, "ddmmyyyy")}</Text></View>
          <View style={styles.col}><Text style={styles.bold}>{getAccountingDate(transaction.created_at, transaction.execution_date)}</Text></View>
          <View style={styles.col}><Text style={styles.bold}>{getAccreditedDate(new Date(transaction.created_at).toDateString() === new Date(transaction.execution_date).toDateString() ? transaction.created_at : transaction.execution_date)}</Text></View>
        </View>
        <Text>Reference</Text>
        <Text style={styles.bold}>{transaction.description?.toUpperCase()}</Text>
      </View>

      {/* Amounts */}
      <View style={[styles.section, styles.borderBox]}>
        <View style={styles.row}>
          <View style={styles.col}><Text>Amount</Text></View>
          <View style={styles.col}><Text>Commissions</Text></View>
          <View style={styles.col}><Text>Total</Text></View>
        </View>
        <View style={styles.row}>
          <View style={styles.col}><Text style={styles.bold}>{formatNumber(transaction.amount)} EURO</Text></View>
          <View style={styles.col}><Text style={styles.bold}>0,00 EURO</Text></View>
          <View style={styles.col}><Text style={styles.bold}>{formatNumber(transaction.amount)} EURO</Text></View>
        </View>
      </View>

      {/* Notes */}
      <View style={[styles.section, styles.borderBox, { paddingTop: 20, paddingBottom: 20 }]}>
        <Text style={[styles.bold, styles.justifyText, { fontSize: 8 }]}>
          • The operation will be performed at the cut-off of 20.00 on {formatDate(transaction.created_at, "ddmmyyyy")} and it will be possible to revoke the provision until 19.59 on {formatDate(transaction.created_at, "ddmmyyyy")}.
        </Text>
      </View>

      <View style={[styles.section, styles.borderBox, { paddingTop: 20, paddingBottom: 20 }]}>
        <Text style={[styles.bold, styles.justifyText, { fontSize: 8 }]}>
          • The operation could be counted and subject to the payment of the "Unit cost per operation" according to the methods agreed upon signing the current account contract and/or subsequent agreed changes, in which you can find every detail in this regard. During the periodic settlement of these expenses, you will be able to check the details of the calculations, which are displayed in your current account statement, under the "Expenses" item in the "Details of closing costs" section.
        </Text>
        <Text style={[styles.bold, styles.justifyText, { marginTop: 5, fontSize: 8 }]}>
          • Transactions arranged on public holidays are considered received on the first following working day
        </Text>
      </View>

      {/* Footer */}
      <View style={styles.pageFooter}>
        <Text>Page 1 of 1</Text>
      </View>
    </Page>
  </Document>
);

export default ReceiptPdf;
